import type { AppRouter } from '@stopal/api'
import { localCalendarDate } from '@stopal/shared/time'
import type { inferRouterOutputs } from '@trpc/server'
import type { Tone } from '@/components/domain'
import type { CustomerCopy } from '@/lib/customer-copy'
import { formatFullDate } from '@/lib/format-date'
import { shouldRetryQuery } from '@/lib/trpc-errors'

type Invoice =
  inferRouterOutputs<AppRouter>['billing']['listInvoices']['items'][number]

export type InvoiceStatus = Invoice['status']
export type InvoiceType = Invoice['type']

/**
 * Invoice status → copy slug, exhaustive so a value added to `invoice_status`
 * fails the BUILD rather than rendering a raw enum word on a customer's bill.
 *
 * Lifted out of the component for the reason `overdue-copy.ts` was: `t` falls
 * back to the SLUG ITSELF when a message is unknown, so a missing catalog entry
 * is not a crash and not a blank — it prints `billing.invoices.status_draft` in
 * a chip on a money surface. That has shipped here before. A `Record` makes a
 * new status a build error; a plain `.ts` module makes a missing catalog entry a
 * TEST failure, which a `.tsx` no unit test can import cannot.
 */
export const STATUS_SLUG: Record<InvoiceStatus, string> = {
  draft: 'billing.invoices.status_draft',
  sent: 'billing.invoices.status_sent',
  paid: 'billing.invoices.status_paid',
  void: 'billing.invoices.status_void',
}

/** The tone each status carries. Separate from the words so Ops may reword
 *  without restyling, and exhaustive for the same reason. */
export const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  draft: 'warning',
  sent: 'warning',
  paid: 'success',
  // Muted, not `critical`: a void period is one we said the customer does not
  // owe, so it must not read as a problem they have to act on.
  void: 'muted',
}

/**
 * Invoice type → the slug for its second line.
 *
 * An exhaustive `Record` rather than `type === 'subscription' ? … : …`, which
 * is the shape `customer-copy.md` forbids outright: a third `invoice_type` (a
 * refund, a credit note) would silently inherit "Order · {date}", a sentence
 * untrue of it, with nothing failing.
 *
 * `periodic` is the wording when the row names a billing window; `dated` is the
 * fallback when it does not. A subscription invoice with a null period is a real
 * data anomaly — `overdue-read.ts` filters those rows out rather than charge
 * them — so it gets its OWN undated storage line. Falling through to the service
 * line instead would tell the customer a storage period was an order.
 */
export const TYPE_LINE_SLUG: Record<
  InvoiceType,
  { dated: string; periodic?: string }
> = {
  service: { dated: 'billing.invoices.line_service' },
  subscription: {
    dated: 'billing.invoices.line_subscription_undated',
    periodic: 'billing.invoices.line_subscription',
  },
}

/** Every other slug the invoice list renders, so the test can check them too. */
export const INVOICE_SLUGS = [
  'billing.invoices.heading',
  'billing.invoices.empty',
  'billing.invoices.empty_body',
  'billing.invoices.load_failed',
  'billing.invoices.status_refunded',
  'billing.invoices.row_refunded',
] as const

/**
 * Every fixed slug the ONE-INVOICE page renders.
 *
 * Its own list rather than an extension of `INVOICE_SLUGS`, because the two
 * surfaces fail independently: the list can render every word it needs while
 * the page a row opens prints raw slugs at a customer. Both are covered by the
 * same test for the same reason — `t` falls back to the slug itself, so a
 * missing catalog entry is neither a crash nor a blank.
 */
export const INVOICE_DETAIL_SLUGS = [
  'billing.invoices.detail_eyebrow',
  'billing.invoices.detail_summary_heading',
  'billing.invoices.detail_lines_heading',
  'billing.invoices.detail_no_lines',
  'billing.invoices.detail_not_found',
  'billing.invoices.detail_load_failed',
  'billing.invoices.detail_paid_with',
  'billing.invoices.detail_refunded',
  'billing.invoices.detail_label_date',
  'billing.invoices.detail_label_period',
  'billing.invoices.detail_label_status',
  'billing.invoices.detail_label_payment',
  'billing.invoices.detail_label_paid_on',
  'billing.invoices.detail_label_due',
  'billing.invoices.detail_col_description',
  'billing.invoices.detail_col_quantity',
  'billing.invoices.detail_col_unit_price',
  'billing.invoices.detail_col_amount',
  'billing.invoices.detail_subtotal',
  'billing.invoices.detail_tax',
  'billing.invoices.detail_total',
  'billing.invoices.detail_refunded_total',
  'billing.invoices.detail_net_total',
  'billing.invoices.detail_print',
  'billing.invoices.detail_back',
] as const

/**
 * Which slug names this row's second line, and whether it takes a period.
 *
 * A pure function rather than a ternary in the component, because the list row
 * is a `.tsx` no unit test imports — the three branches below shipped once with
 * nothing executing them, including a catalog slug added specifically for the
 * undated case. Here they are falsifiable at 0ms.
 *
 * A type this build does not know returns `null`, which the caller renders as a
 * bare date. That cannot happen while the `Record` is exhaustive, and it is not
 * dead defence: the API can ship a new `invoice_type` before a viewer's cached
 * bundle knows it, which is the same window `statusChipFor` guards.
 */
export function lineSlugFor(
  type: InvoiceType,
  hasPeriod: boolean,
): { slug: string; withPeriod: boolean } | null {
  const line = TYPE_LINE_SLUG[type]
  if (!line) return null
  if (line.periodic && hasPeriod) {
    return { slug: line.periodic, withPeriod: true }
  }
  return { slug: line.dated, withPeriod: false }
}

/**
 * The invoice's day and, when it covers one, its billing window — as printed.
 *
 * `period_start` / `period_end` are Postgres `date` columns — bare calendar
 * days — so they go through `formatFullDate`, the repo's local-midnight anchor,
 * and never through `new Date()`, which renders the previous day in every zone
 * Stopal operates in. `createdAt` IS an instant and is reduced to the viewer's
 * own day first.
 */
export function invoiceDates(
  invoice: Pick<Invoice, 'createdAt' | 'periodStart' | 'periodEnd'>,
): {
  day: string
  period: string | null
} {
  return {
    day: formatFullDate(localCalendarDate(invoice.createdAt)),
    period:
      invoice.periodStart && invoice.periodEnd
        ? `${formatFullDate(invoice.periodStart)} – ${formatFullDate(invoice.periodEnd)}`
        : null,
  }
}

/**
 * The one sentence saying what an invoice is for.
 *
 * Read by the household list row, the business table's For column and the
 * invoice page's subtitle, so the row a customer opened and the page it opened
 * describe the invoice with one sentence rather than three that can drift.
 * WHICH line is `lineSlugFor`'s decision; this only fills it in.
 */
export function invoiceLineFor(
  invoice: Pick<Invoice, 'type' | 'createdAt' | 'periodStart' | 'periodEnd'>,
  t: CustomerCopy['t'],
): string {
  const { day, period } = invoiceDates(invoice)
  const line = lineSlugFor(invoice.type, period !== null)
  if (!line) return day
  return line.withPeriod && period !== null
    ? t(line.slug, { period })
    : t(line.slug, { date: day })
}

/**
 * The words and tone for a status, degrading rather than throwing.
 *
 * `STATUS_SLUG[status]` is exhaustive at BUILD time, which says nothing about a
 * bundle a viewer already has cached: ship a new `invoice_status` on the API
 * before the web build rolls and an unguarded lookup dereferences `undefined`
 * and white-screens `/app` through its route boundary. Degrade instead
 * (`STATUS_SLUG[status] ?? null`): a billing page is where a blank screen is
 * worst. `slug: null` means "render the raw value".
 */
export function statusChipFor(status: InvoiceStatus): {
  slug: string | null
  tone: Tone
} {
  return {
    slug: STATUS_SLUG[status] ?? null,
    tone: STATUS_TONE[status] ?? 'neutral',
  }
}

/**
 * How the invoice says it was paid — or `null`, meaning say nothing.
 *
 * BOTH columns or neither. `payment_method_brand` and `payment_method_last4`
 * are independently nullable, and half a sentence is worse than a dash: "Paid
 * with Visa ending" reads as a rendering bug, and "Paid with ending 4242" reads
 * as one too. The caller renders the row's empty placeholder instead.
 *
 * **The null pair means "no card on record", NOT "nothing was charged".** A
 * PAID invoice can still carry neither: a storage period settled by a bank
 * debit or wholly by credit, one whose card could not be read back from Stripe
 * when it settled, or one settled before storage charges recorded the card. It
 * prints the dash beside its Paid status — the honest rendering of what is
 * stored; saying "not charged" there would be false.
 *
 * **Every member of the account reads the card, not only whoever's it was —
 * ruled by Marcus.** The server hands the two columns to anybody who
 * can open the invoice, so there is no third state to distinguish here: a
 * colleague sees exactly what the payer sees. The reasoning, and
 * why `loadOverdueCharges`' own login narrowing is a different question, is in
 * `invoice-detail.ts` beside the read.
 *
 * The brand is stored as Stripe writes it — `visa`, `mastercard`, `amex`, all
 * lower case — and "Paid with visa ending 4242" reads as a database field on a
 * page that is otherwise a document. Capitalised HERE rather than with a CSS
 * `capitalize`, which would title-case the Ops-editable sentence around it.
 *
 * Pure, so the four combinations are falsifiable without a renderer — the
 * reason every other decision on this surface lives in this module.
 */
export function paymentLineFor(
  brand: string | null,
  last4: string | null,
): { slug: string; vars: { cardBrand: string; last4: string } } | null {
  if (!brand || !last4) return null
  return {
    slug: 'billing.invoices.detail_paid_with',
    vars: { cardBrand: brand.charAt(0).toUpperCase() + brand.slice(1), last4 },
  }
}

/** The optional rows the summary block carries, in the order they print. */
export type SummaryRowKey =
  | 'billed-on'
  | 'period'
  | 'paid-on'
  | 'due'
  | 'status'
  | 'payment'

/**
 * Which rows the invoice's summary block shows.
 *
 * A pure function so each branch is falsifiable at 0ms without a renderer —
 * `invoice-detail.test.tsx` renders the page, but only for the shapes it
 * builds. These four are all conditional, and each one conditional on a
 * different column:
 *
 * - **`period`** on a `period_start` / `period_end` PAIR. A one-off charge
 *   covers no window, and an empty "Billing period" row invites the reader to
 *   wonder what it should have said. Subscription invoices — the commonest kind
 *   the product mints — are the only ones that carry it.
 * - **`paid-on`** on `paid_at`, which is an instant.
 * - **`due`** on `due_date`, and only while the invoice is NOT settled: a date
 *   an invoice was due by, months after it was paid, is noise at best and reads
 *   as an outstanding demand at worst.
 * - **`payment`** always, because its own empty state (the dash) is meaningful:
 *   it says no card is on record (see `paymentLineFor` — not "not charged").
 */
export function summaryRowsFor(invoice: {
  periodStart: string | null
  periodEnd: string | null
  paidAt: Date | null
  dueDate: string | null
}): readonly SummaryRowKey[] {
  const rows: SummaryRowKey[] = ['billed-on']
  if (invoice.periodStart && invoice.periodEnd) rows.push('period')
  if (invoice.paidAt) rows.push('paid-on')
  else if (invoice.dueDate) rows.push('due')
  rows.push('status', 'payment')
  return rows
}

/** As much of a stored line as the money decisions below need. */
export interface InvoiceLineAmount {
  id: string
  description: string
  amount: string
}

/** One row of the footer beneath the itemisation. */
export interface InvoiceFooterRow {
  key: string
  /** A catalog slug, for a fixed label. Null on an adjustment. */
  slug: string | null
  /** The line's OWN stored description. Null on a fixed row. */
  label: string | null
  amount: number
  /** The bottom line — what the customer actually parted with. */
  emphasis: boolean
}

/**
 * How an invoice's money divides between the item table and the footer.
 *
 * **This is a LAYOUT decision and changes no arithmetic.** `invoice.subtotal`
 * is stored GROSS — the sum of the POSITIVE lines only — while a discount and a
 * redeemed credit are ordinary `invoice_line_item` rows carrying a negative
 * amount (`checkout.service.ts`, which says so at both writes). Printing those
 * rows inside the itemisation and then Subtotal / Tax / Total beneath it gives
 * a bill whose subtotal is not the sum of the lines above it and whose
 * subtotal + tax does not reach its total, with nothing on the page explaining
 * the gap. Lifting the negative lines into the footer between Subtotal and
 * Total is how every bill a customer has ever read resolves it, and the column
 * then adds up top to bottom.
 *
 * **The refund is the second half, and it exists because two customer surfaces
 * were stating different money for one purchase.** `settleEventRefunds` stamps
 * `invoice_line_item.refunded_at` and touches neither `invoice.total` nor
 * `invoice.status` — deliberately, per `billing.md`: the settlement is the
 * line's, and the status cannot answer "did the money come back". So `total` on
 * a refunded invoice is the PRE-refund figure. The server sums the stamped
 * lines (`refundedTotal`, `invoice-refunds.ts` — the same number
 * `orderService.getByUser` derives for `/app/orders/[id]`) and hands it to the
 * list row and to this page alike; {@link netTotalOf} takes it off the total
 * for both, so the row a customer taps and the page it opens cannot disagree.
 *
 * **Every figure is taken in whole CENTS before it is added or signed.** Float
 * arithmetic over two-decimal strings leaves residue — a fully refunded order
 * carrying a discount netted to `-7e-15`, which printed `-$0.00` — and the
 * order page already works in cents for the same reason.
 *
 * Nothing here recomputes a total from the lines. The stored figures are what
 * the customer was charged, and a page that re-adds them would disagree with
 * the card statement the first time a writer rounds differently.
 */
export function invoiceMoney<T extends InvoiceLineAmount>(invoice: {
  subtotal: string
  tax: string
  total: string
  refundedTotal: string
  lines: readonly T[]
}): {
  items: readonly T[]
  footer: readonly InvoiceFooterRow[]
  /** `total` less anything refunded — the headline figure. */
  netTotal: number
} {
  const items: T[] = []
  const adjustments: T[] = []
  for (const line of invoice.lines) {
    // A value that does not parse is NOT an adjustment: `Number('x') < 0` is
    // false, so it stays in the itemisation and prints as stored, which is what
    // `formatInvoiceAmount` does with it too.
    if (Number(line.amount) < 0) adjustments.push(line)
    else items.push(line)
  }

  const refundedCents = centsOf(invoice.refundedTotal)
  const netTotal = netTotalOf(invoice)

  const footer: InvoiceFooterRow[] = [
    fixedRow('subtotal', 'billing.invoices.detail_subtotal', invoice.subtotal),
    ...adjustments.map((line) => ({
      // The line's OWN description, not a catalog word. `customer-copy.md`'s
      // rule: a closed set of fixed words belongs in the catalog, an open set
      // belongs on the row — and these labels are data ("First order
      // discount" comes from the discount row ops created). Collapsing them to
      // a generic "Discount" would lose which discount, and there is nothing
      // on a negative line that says whether it is a discount or a credit.
      key: `adjustment-${line.id}`,
      slug: null,
      label: line.description,
      amount: centsOf(line.amount) / 100,
      emphasis: false,
    })),
    fixedRow('tax', 'billing.invoices.detail_tax', invoice.tax),
    {
      ...fixedRow('total', 'billing.invoices.detail_total', invoice.total),
      // Emphasised only when it IS the bottom line. Under a refund the net
      // below is what the customer parted with, and two bold figures disagreeing
      // by the refund is the contradiction this whole function exists to end.
      emphasis: refundedCents === 0,
    },
  ]

  if (refundedCents !== 0) {
    footer.push(
      {
        key: 'refunded',
        slug: 'billing.invoices.detail_refunded_total',
        label: null,
        // Negative, so the column still adds up downwards: money that came back
        // is a subtraction from the total above it.
        amount: -refundedCents / 100,
        emphasis: false,
      },
      {
        key: 'net',
        slug: 'billing.invoices.detail_net_total',
        label: null,
        amount: netTotal,
        emphasis: true,
      },
    )
  }

  return { items, footer, netTotal }
}

/**
 * What the customer parted with for an invoice: its stored total less what the
 * server says came back (`refundedTotal`), in whole cents.
 *
 * The ONE place that subtraction happens, read by the list row and by the page
 * it opens — which is the whole reason it is a function rather than an
 * expression: a row printing the stored total above a page printing the net is
 * two customer surfaces stating different money for one invoice.
 */
export function netTotalOf(invoice: {
  total: string
  refundedTotal: string
}): number {
  return (centsOf(invoice.total) - centsOf(invoice.refundedTotal)) / 100
}

/**
 * What an invoice says about a refund — on its list row, beside the net
 * amount, and in the Status row of the page that row opens, so the two cannot
 * name one invoice two ways.
 *
 * The row prints the NET ({@link netTotalOf}), so a fully refunded invoice
 * reads `$0.00` — and beside a green "Paid" chip that pair says nothing
 * happened. So:
 *
 * - **all of a PAID invoice came back** → the chip says "Refunded", muted like
 *   a void period: nothing for the customer to do. No sub-line; the chip has
 *   said it.
 * - **some of it came back** → the status stands, and a sub-line names how
 *   much was refunded, since the figure above it is already the remainder.
 * - **nothing came back** → exactly {@link statusChipFor}.
 *
 * Only what is SHOWN changes. `invoice.status` stays `paid` — a refund stamps
 * the lines, never the status (`billing.md`, "Did the money come back?") — and
 * an unsettled invoice with a stamped line keeps its own status word, with the
 * list row's sub-line saying what came back.
 *
 * A cancel refunds the invoice's LINES, and tax is not a line, so a fully
 * cancelled order that was taxed keeps its tax and reads as partly refunded.
 * That is true to the money moved.
 */
export function invoiceStatusFor(invoice: {
  status: InvoiceStatus
  total: string
  refundedTotal: string
}): {
  chip: ReturnType<typeof statusChipFor>
  refundNote: { slug: string; vars: { amount: string } } | null
} {
  const refundedCents = centsOf(invoice.refundedTotal)
  const chip = statusChipFor(invoice.status)
  if (refundedCents <= 0) return { chip, refundNote: null }

  if (invoice.status === 'paid' && netTotalOf(invoice) <= 0) {
    return {
      chip: { slug: 'billing.invoices.status_refunded', tone: 'muted' },
      refundNote: null,
    }
  }
  return {
    chip,
    refundNote: {
      slug: 'billing.invoices.row_refunded',
      vars: { amount: formatInvoiceAmount(refundedCents / 100) },
    },
  }
}

function fixedRow(key: string, slug: string, stored: string): InvoiceFooterRow {
  return {
    key,
    slug,
    label: null,
    amount: centsOf(stored) / 100,
    emphasis: false,
  }
}

/**
 * A stored `numeric` as whole cents — 0 for a value that does not parse, which
 * `numeric` cannot produce (the cached-bundle case `formatInvoiceAmount`
 * names). Integer, so a sum or a difference of them is exact.
 */
function centsOf(stored: string): number {
  const n = Number(stored)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

/**
 * One stored money value, as an invoice has to print it.
 *
 * Neither existing formatter can be used here, and both fail on the values an
 * invoice actually carries:
 *
 * - **`formatValue(x, 'money')`** (`components/domain`) returns the EMPTY
 *   STRING for zero, deliberately — "zero in money columns is treated as no
 *   data". On a bill it is data: a free trip, a fully-covered period and a $0
 *   tax line are all real, and a blank where `$0.00` belongs reads as a column
 *   that failed to render.
 * - **`formatUsd`** (the list's formatter) emits no `$` and no sign handling,
 *   so its callers prefix one — which turns a discount into `$-23.00`.
 *
 * A discount and a redeemed credit are ordinary lines carrying a NEGATIVE
 * amount, so the sign goes OUTSIDE the currency symbol (`-$23.00`), which is
 * how every bill a customer has ever read writes it.
 *
 * A value that does not parse is returned AS STORED rather than as a guess.
 * `numeric` cannot produce one, so this is the cached-bundle case again — and
 * on a money surface, printing the stored characters is the only honest
 * fallback available.
 *
 * It takes a NUMBER as well, for the footer rows {@link invoiceMoney} derives
 * (a net, a refunded sum) — one formatter, so a derived figure and a stored one
 * cannot come to be written differently on the same column.
 */
export function formatInvoiceAmount(stored: string | number): string {
  const n = Number(stored)
  if (!Number.isFinite(n)) return String(stored)
  // The SIGN is read off the rounded cents, never off the raw value: a figure
  // carrying float residue below half a cent (`-7e-15`) is zero on paper, and
  // signing it first printed `-$0.00`.
  const cents = Math.round(n * 100)
  const figure = (Math.abs(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return cents < 0 ? `-$${figure}` : `$${figure}`
}

/**
 * A line's quantity, as stored.
 *
 * `numeric(8,2)` arrives as `"2.00"`, and an invoice that says "2.00" reads as
 * a rendering slip rather than as precision — while a genuine fraction ("1.5"
 * hours, "0.5" of a period) has to survive. `Number` does both; the guard is
 * for the same unparseable case the amount formatter names.
 */
export function formatLineQuantity(stored: string): string {
  const n = Number(stored)
  return Number.isFinite(n) ? String(n) : stored
}

/**
 * Which sentence a failed invoice read gets.
 *
 * `NOT_FOUND` is an ANSWER — the invoice is not this account's, or there is no
 * such invoice, and the server deliberately does not distinguish those (see
 * `invoice-detail.ts`). Everything else is "we do not know", and saying the
 * invoice could not be found there would be the same class of lie as reporting
 * an empty list for a read that failed.
 *
 * **`BAD_REQUEST` is the same ANSWER by a different door, and it is the one a
 * real customer meets.** The id comes off the URL and the procedure's input is
 * `z.uuid()`, so a link truncated by an email client, a pasted fragment, or a
 * hand-typed id fails validation rather than the lookup — and the procedure has
 * no other source of `BAD_REQUEST`, its service throwing only `NotFoundError`.
 * Answering a definite "that is not an invoice id" with "try again" invites the
 * customer to keep retrying a link that can never work.
 *
 * The server's own message is deliberately NOT shown: a domain error's sentence
 * is written where the guard is, and this one belongs to Ops like the rest of
 * the invoice's words.
 *
 * Reads `data.code` STRUCTURALLY rather than through `instanceof
 * TRPCClientError`, following `isDatabaseConflict` (`lib/trpc-errors.ts`): the
 * field exists exactly when the server put it there, and a structural read is
 * one a unit test can drive without constructing a client error.
 */
export function invoiceFailureSlug(error: unknown): string {
  return invoiceAnswered(error)
    ? 'billing.invoices.detail_not_found'
    : 'billing.invoices.detail_load_failed'
}

/**
 * The codes on which the server has ANSWERED about this invoice.
 *
 * Read twice, and that is the point of naming it: it decides the sentence
 * above, and it decides whether the read is worth repeating. Retrying either of
 * these three times over exponential backoff cannot change the answer and
 * leaves the customer watching a spinner for several seconds before a verdict
 * the first response already carried.
 */
function invoiceAnswered(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('data' in error))
    return false
  const code = (error as { data?: { code?: string } }).data?.code
  return code === 'NOT_FOUND' || code === 'BAD_REQUEST'
}

/**
 * Whether to retry a failed read of ONE invoice.
 *
 * The shared client retries every non-auth, non-timeout failure three times
 * (`trpc/query-client.ts`), which is right for a read whose answer can change
 * and wrong for this one: a `NOT_FOUND` is a settled fact about an id, and a
 * `BAD_REQUEST` is a settled fact about its shape. Both were being retried, so
 * the not-found sentence landed seconds after the tap — long enough that the
 * spec covering it needed a 25-second wait to see it at all, which is the
 * symptom rather than the property.
 *
 * It DELEGATES the rest rather than restating it. Passing a `retry` at a call
 * site replaces the default wholesale, so a hand-written `failureCount < 3`
 * here would silently drop the UNAUTHORIZED and client-timeout exclusions the
 * default carries and re-introduce both of the failures they exist to prevent.
 */
export function retryInvoiceRead(
  failureCount: number,
  error: unknown,
): boolean {
  return !invoiceAnswered(error) && shouldRetryQuery(failureCount, error)
}
